/**
 * proposalContract — Assist Form Contract v1 (12/09/2026)
 *
 * Tipos, constantes e funções PURAS do envelope de proposta.
 *
 *   Assist ↔ Shell ↔ AssistFormBridge ↔ Adapter ↔ Formulário
 *
 * Regra universal: o Assist propõe; o usuário revisa; o formulário aplica;
 * o usuário salva pelo fluxo canônico. Nada aqui persiste nem chama API.
 *
 * Estados:  PROPOSED → APPLIED → (SAVED pelo formulário)
 *           PROPOSED → REJECTED | STALE
 *           APPLIED  → UNDONE
 */

export const CONTRACT_VERSION = '1.0';

export const PROPOSAL_STATUS = Object.freeze({
  PROPOSED: 'PROPOSED',
  APPLIED: 'APPLIED',
  PARTIALLY_APPLIED: 'PARTIALLY_APPLIED',
  REJECTED: 'REJECTED',
  STALE: 'STALE',
  UNDONE: 'UNDONE',
});

export const OPS = Object.freeze(['set', 'add', 'remove', 'replace', 'reorder', 'select_entity']);

export const ERROR_CODES = Object.freeze({
  FORM_NOT_ACTIVE: 'FORM_NOT_ACTIVE',
  FORM_INSTANCE_CHANGED: 'FORM_INSTANCE_CHANGED',
  FORM_CHANGED: 'FORM_CHANGED',
  SUPERSEDED: 'SUPERSEDED',
  STALE_PROPOSAL: 'STALE_PROPOSAL',
  INVALID_PATH: 'INVALID_PATH',
  UNKNOWN_PATH: 'UNKNOWN_PATH',
  OPERATION_NOT_ALLOWED: 'OPERATION_NOT_ALLOWED',
  INVALID_TYPE: 'INVALID_TYPE',
  INVALID_ENUM: 'INVALID_ENUM',
  DOMAIN_VALIDATION_FAILED: 'DOMAIN_VALIDATION_FAILED',
  ENTITY_NOT_RESOLVED: 'ENTITY_NOT_RESOLVED',
  ENTITY_AMBIGUOUS: 'ENTITY_AMBIGUOUS',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  PROPOSAL_ALREADY_APPLIED: 'PROPOSAL_ALREADY_APPLIED',
  PROPOSAL_EXPIRED: 'PROPOSAL_EXPIRED',
  UNDO_NOT_AVAILABLE: 'UNDO_NOT_AVAILABLE',
  REJECTED_BY_USER: 'REJECTED_BY_USER',
});

const uuid = () => (
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
);

/** ID de proposta — gerado pelo FRONTEND na fase compatibility; nunca pelo LLM. */
export const newProposalId = () => `prop:${uuid()}`;
export const newOperationId = () => `op:${uuid()}`;
/** Identidade local de elemento de coleção — criada pelo adapter, nunca pelo LLM. */
export const newLocalId = (prefix = 'el') => `${prefix}:${uuid()}`;

/**
 * Monta o envelope a partir do contexto CAPTURADO NO send() (nunca do
 * momento da chegada) + operations já convertidas pelo compatibility
 * adapter / adapter de domínio.
 */
export function buildProposal({ captured, operations, entity = null, warnings = [], missingFields = [], explanation = '', final = false }) {
  if (!captured || !captured.formId) return null;
  return {
    contractVersion: CONTRACT_VERSION,
    proposalId: newProposalId(),
    formId: captured.formId,
    formInstanceId: captured.formInstanceId || null,
    entity,
    baseRevision: captured.baseRevision || null,
    interactionGeneration: captured.interactionGeneration ?? null,
    focusAtSend: captured.focus || null,
    operations: (operations || []).map((op) => ({ operationId: op.operationId || newOperationId(), ...op })),
    warnings: Array.isArray(warnings) ? warnings : [],
    missingFields: Array.isArray(missingFields) ? missingFields : [],
    explanation: explanation || '',
    final: final === true,
    status: PROPOSAL_STATUS.PROPOSED,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Avaliação de staleness (pura). `active` descreve o formulário ativo AGORA.
 * @returns {{stale:boolean, reason?:string}}
 */
export function evaluateStaleness(proposal, active) {
  if (!proposal) return { stale: true, reason: ERROR_CODES.STALE_PROPOSAL };
  if (proposal.status === PROPOSAL_STATUS.APPLIED || proposal.status === PROPOSAL_STATUS.PARTIALLY_APPLIED) {
    return { stale: true, reason: ERROR_CODES.PROPOSAL_ALREADY_APPLIED };
  }
  if (proposal.status === PROPOSAL_STATUS.REJECTED) return { stale: true, reason: ERROR_CODES.REJECTED_BY_USER };
  if (proposal.status === PROPOSAL_STATUS.STALE) return { stale: true, reason: proposal.staleReason || ERROR_CODES.STALE_PROPOSAL };
  if (!active || !active.formId) return { stale: true, reason: ERROR_CODES.FORM_NOT_ACTIVE };
  if (active.formId !== proposal.formId) return { stale: true, reason: ERROR_CODES.FORM_NOT_ACTIVE };
  if (proposal.formInstanceId && active.formInstanceId !== proposal.formInstanceId) {
    return { stale: true, reason: ERROR_CODES.FORM_INSTANCE_CHANGED };
  }
  if (active.interactionGeneration != null && proposal.interactionGeneration != null
      && proposal.interactionGeneration !== active.interactionGeneration) {
    return { stale: true, reason: ERROR_CODES.SUPERSEDED };
  }
  if (proposal.baseRevision && active.revision && proposal.baseRevision !== active.revision) {
    return { stale: true, reason: ERROR_CODES.FORM_CHANGED };
  }
  return { stale: false };
}

/** Inversa de uma operação — gerada pelo ADAPTER no apply (nunca pelo LLM). */
export function inverseOf(op, actualCurrentValue) {
  switch (op.op) {
    case 'set':
    case 'replace':
      return {
        operationId: newOperationId(),
        op: op.op,
        path: op.path,
        current_value: op.proposed_value,
        proposed_value: actualCurrentValue,
        reason: `undo:${op.operationId}`,
        source: 'undo',
      };
    default:
      // add/remove/reorder: inversas específicas ficam com adapters que os
      // suportarem (nenhum no F03). Retornar null impede undo silencioso.
      return null;
  }
}
